import { DEFAULT_MODAL_TRANSITION_TIMING } from 'foundations-components/constants';
import { getAttachedElement } from './elementUtils';

/**
 * Returns the delay needed due to the animation of a modal opening - attempting to attach to an animating modal
 * can have unpredictable results. The DEFAULT_MODAL_TRANSITION_TIMING is a string ending in 'ms'.
 */
function getAnimationDelay() {
  const timeString = DEFAULT_MODAL_TRANSITION_TIMING;
  return parseInt(timeString.slice(0, timeString.length - 2), 10) + 100;
}

/**
 * Waits for React render cycles to complete and tour components to be initialized.
 * Includes support for modal animations when needed.
 */
function waitForReactReady(element, requiresModalAnimation = false) {
  const timeout = requiresModalAnimation ? getAnimationDelay() : 0;
  return new Promise(resolve => {
    // Wait for React render cycle to complete
    setTimeout(() => {
      // Wait for animation frame (when pulser is initialized)
      requestAnimationFrame(() => {
        resolve(element);
      });
    }, timeout);
  });
}
/**
 * Watches DOM changes for when a specificed elementQuery is matched.
 *
 * @return a promise with the element
 */
export const waitForElementToAppear = ({
  elementQuery,
  elementGetter,
  requiresModalAnimation = false,
  targetDocument = document,
  logger = console.debug
}) => {
  const attachTo = elementQuery || elementGetter;
  const logItem = typeof attachTo === 'function' ? attachTo.toString() : attachTo;
  logger('Waiting for element: ', logItem);
  const maybeElement = getAttachedElement(attachTo, targetDocument);
  if (maybeElement) {
    return waitForReactReady(maybeElement, requiresModalAnimation);
  }
  return new Promise(resolve => {
    const observer = new MutationObserver(() => {
      const maybeAppearedElement = getAttachedElement(attachTo, targetDocument);
      if (maybeAppearedElement) {
        logger('Element is visible: ', attachTo);
        observer.disconnect();
        waitForReactReady(maybeAppearedElement, requiresModalAnimation).then(resolve).catch(resolve);
      }
    });
    observer.observe(targetDocument, {
      attributes: true,
      childList: true,
      characterData: false,
      subtree: true
    });
  });
};